import {
  format,
  addMonths,
  addDays,
  nextSunday,
  startOfWeek,
  isMonday,
  differenceInDays,
  formatDistanceStrict,
  parse,
  parseISO,
  differenceInCalendarDays,
  formatDistance,
} from 'date-fns';
import { enUS, es, pt, ru, de, zhCN, fr, hi } from 'date-fns/locale';

export const getLocale = (language) => {
  switch (language) {
    case 'en':
      return enUS;
    case 'es':
      return es;
    case 'pt':
      return pt;
    case 'ru':
      return ru;
    case 'de':
      return de;
    case 'cn':
      return zhCN;
    case 'fr':
      return fr;
    case 'hi':
      return hi;
    default:
      return enUS;
  }
};

export const getMonths = (language) => {
  const locale = getLocale(language);

  const months = [];
  let date = alignTimezone(new Date('2021-01-01'));

  for (let i = 0; i < 12; i++) {
    months.push(format(date, 'MMMM', { locale }));
    date = addMonths(date, 1);
  }
  return months;
};

export const getWeekDays = (language) => {
  const locale = getLocale(language);

  const days = [];
  let date = new Date('2021-01-01');
  date = nextSunday(date);

  for (let i = 0; i < 7; i++) {
    days.push(format(date, 'EEEE', { locale }));
    date = addDays(date, 1);
  }

  return days;
};

export const getShortWeekDays = (language) => {
  const locale = getLocale(language);

  const days = [];
  let date = new Date('2021-01-01');
  date = nextSunday(date);

  for (let i = 0; i < 7; i++) {
    days.push(format(date, 'EEE', { locale }));
    date = addDays(date, 1);
  }

  return days;
};

export const getWeekStartsOn = (language) => {
  const locale = getLocale(language);

  let date = new Date('2021-01-01');
  date = startOfWeek(date, { locale });

  if (isMonday(date)) {
    return 1;
  }
  return 0;
};

export const alignTimezone = (date, reverse = false) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  let offset = date.getTimezoneOffset() * 60 * 1000;
  if (reverse) {
    offset *= -1;
  }
  return new Date(date.valueOf() + offset);
};

export const validateDate = (item, language, timeFormat = 'HH:mm:ss', dateFormat = 'MMM dd') => {
  const date = new Date(item);
  const now = new Date();
  const differenceInDaysNumber = differenceInDays(now, date);

  if (isYesterday(date)) {
    return 'Ayer';
  } else if (differenceInDaysNumber === 0) {
    return format(new Date(item), timeFormat, { locale: getLocale(language) });
  } else {
    return format(date, dateFormat, {
      locale: getLocale(language),
    });
  }
};

export const dateinText = (date, language, timeFormat = 'HH:mm') => {
  const dateObj = new Date(date);
  const now = new Date();
  const differenceInDaysNumber = differenceInDays(now, dateObj);

  let firstText = '';

  if (isYesterday(dateObj, now)) {
    return language === 'es' ? 'Ayer' : 'Yesterday';
  } else if (differenceInDaysNumber === 0) {
    return language === 'es' ? 'Hoy' : 'Today';
  } else {
    if (language === 'es') {
      firstText = format(dateObj, "d 'de' MMMM", {
        locale: getLocale(language),
      });
    } else {
      firstText = format(dateObj, 'MMMM d', {
        locale: getLocale(language),
      });
    }
  }

  return firstText;
};

export const getFormatedDate = (date) => {
  if (date.endsWith('Z') || date.slice(-6) === '+00:00') return date;
  return date + '+00:00';
};

export const getExpirationTime = (expirationDate, language) => {
  let formatedDate = getFormatedDate(expirationDate);

  const expiration = new Date(formatedDate);
  const today = new Date();
  const difference = expiration - today;

  if (difference >= 3600000 && difference <= 86400000) {
    const remainingMinutes = Math.floor(difference / 60000) % 60;
    const hoursText = formatDistanceStrict(expiration, today, {
      unit: 'hour',
      locale: getLocale(language),
      roundingMethod: 'floor',
    });
    return remainingMinutes !== 0 ? `${hoursText} ${remainingMinutes} min` : hoursText;
  }

  return formatDistanceStrict(expiration, today, {
    locale: getLocale(language),
    roundingMethod: 'floor',
  });
};

export const getDateFromOffset = (offset = 0) => {
  let date = new Date();
  date = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  date = date.setMinutes(date.getMinutes() + offset);

  return date;
};

export const toStringDate = (day) => {
  return format(day, 'yyyy-MM-dd');
};

export const generateDisabledDates = (startDate, endDate, locale) => {
  const disabledDates = [];
  const currentDate = new Date(startDate);
  while (currentDate < endDate) {
    const formattedDate = format(currentDate, 'yyyy-MM-dd', { locale });
    disabledDates.push(formattedDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return disabledDates;
};

export const convertTime12to24 = (time12h) => {
  const [time, modifier] = time12h.split(' ');

  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = '00';
  }

  if (modifier.toUpperCase() === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }

  var stringHours = hours.length === 1 ? +'0' + hours : hours;

  return stringHours + ':' + minutes + ':00';
};

export const getMonthYear = (month, year, locale) => {
  const date = new Date(year, month - 1, 1);
  return format(date, 'MMMM yyyy', { locale: locale });
};

export const getDatePerformance = (date, performance) => {
  const dateObj = date && parse(date, 'yyyy-MM-dd', new Date());
  const performanceObj = performance && parse(performance, 'HH:mm', new Date());
  const shouldAddDay =
    performanceObj && performanceObj.getHours() >= 0 && performanceObj.getHours() <= 3;
  const nextDayDateObj = dateObj && (shouldAddDay ? addDays(dateObj, 1) : dateObj);
  const datePerformance =
    nextDayDateObj &&
    new Date(
      nextDayDateObj.getFullYear(),
      nextDayDateObj.getMonth(),
      nextDayDateObj.getDate(),
      performanceObj.getHours(),
      performanceObj.getMinutes(),
      0,
    );
  const datePerformancesFormatted = date && format(datePerformance, "yyyy-MM-dd'T'HH:mm:ss.SSS");
  return datePerformancesFormatted;
};

export const isTodayFormat = (date, locale, t, today) => {
  if (today) {
    return t('today');
  } else {
    return format(parseISO(date), 'dd MMM', { locale });
  }
};

const capitalizeExceptDe = (text) => {
  const words = text.split(' ');
  const capitalizedWords = words.map((word) => {
    if (['de', 'las', 'a'].includes(word.toLowerCase())) {
      return word.toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  return capitalizedWords.join(' ');
};

// 23 Abr, 2024
export const formatDateShort = (date, locale) => {
  const dateFromatted = format(
    new Date(date),
    locale.code === 'es' ? 'dd MMM, yyyy' : 'MMM dd, yyyy',
    {
      locale,
    },
  );
  return capitalizeExceptDe(dateFromatted);
};

//23 de Abril de 2024
export const formatDateLong = (date, locale) => {
  const dateFromatted = format(
    new Date(date),
    locale.code === 'es' ? `dd 'de' MMMM 'de' yyyy` : `MMMM dd, yyyy`,
    {
      locale,
    },
  );
  return capitalizeExceptDe(dateFromatted);
};

//04 / 24
export const formatDateMonthYear = (date, locale) => {
  const dateFromatted = format(new Date(date), `MM / yy`);
  return capitalizeExceptDe(dateFromatted);
};

//Martes, 23 de Abr de 2024
export const formatDateWeekday = (date, locale) => {
  const dateFromatted = format(
    new Date(date),
    locale.code === 'es' ? `EEEE, dd 'de' MMM 'de' yyyy` : `EEEE, MMM dd, yyyy`,
    {
      locale,
    },
  );
  return capitalizeExceptDe(dateFromatted);
};

//23 de Abril de 2024 - 13:53
export const formatDateHours = (date, locale) => {
  const dateFromatted = format(
    new Date(date),
    locale.code === 'es' ? `dd 'de' MMMM 'de' yyyy - HH:mm` : `MMMM dd, yyyy - HH:mm`,
    {
      locale,
    },
  );
  return capitalizeExceptDe(dateFromatted);
};

//Abril 23, 2024
export const formatDateDefault = (date, locale) => {
  const dateFromatted = format(new Date(date), 'dd MMM yyyy', {
    locale,
  });
  return capitalizeExceptDe(dateFromatted);
};

//Martes, 23 de Abril
export const formatDateNormal = (date, locale) => {
  const dateFormat = format(
    new Date(date),
    locale.code === 'es' ? "EEEE, dd 'de' MMMM" : 'EEEE, dd MMMM',
    { locale },
  );
  return capitalizeExceptDe(dateFormat);
};

//Mar, 23 de Abril
export const formatDateNormalShort = (date, locale) => {
  const dateFormat = format(
    new Date(date),
    locale.code === 'es' ? "EEE, dd 'de' MMMM" : 'EEE, dd MMMM',
    { locale },
  );
  return capitalizeExceptDe(dateFormat);
};

//Martes 23 de Abr
export const formatDatSemiShort = (date, locale) => {
  const dateFormat = format(new Date(date), locale.code === 'es' ? 'EEEE dd MMM' : 'EEEE, dd MMM', {
    locale,
  });
  return capitalizeExceptDe(dateFormat);
};

//Mar 15 Abr
export const formatShortDayName = (date, locale) => {
  const dateFormat = format(new Date(date), locale.code === 'es' ? 'EEE dd MMM' : 'EEE, MMM dd', {
    locale,
  });
  return capitalizeExceptDe(dateFormat);
};

export const calendarDate = (date, locale) => {
  const dateFormat = format(new Date(date), 'MMM dd, yyyy', { locale });
  return capitalizeExceptDe(dateFormat);
};

export const campaignDate = (date, locale) => {
  const dateFormat = format(new Date(date), 'MMM dd, yy', { locale });
  return capitalizeExceptDe(dateFormat);
};

export const reservationDetailDate = (date, locale) => {
  const formattedDate = format(
    new Date(date),
    locale.code === 'es' ? "EEEE, d 'de' MMMM 'a las' HH:mm 'hr'" : "EEEE, d MMMM 'at' HH:mm 'hr'",
    {
      locale,
    },
  );
  return capitalizeExceptDe(formattedDate);
};

export const reservationDetailDateShort = (date, locale) => {
  const formattedDate = format(
    new Date(date),
    locale.code === 'es' ? "EEE, d 'de' MMMM 'a las'" : "EEE, d MMMM 'at' ",
    {
      locale,
    },
  );
  return formattedDate;
};

export const addMinutes = (date, minutes) => {
  let result = new Date(date);
  result.setMinutes(date.getMinutes() + minutes);
  return result;
};

export const formatDateTime = (date, time = '00:00:00') => {
  return new Date(date + 'T' + time);
};

export const isTodayDate = (now, date) => {
  return format(now, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
};

export const isMoreThanHours = (date1, date2, hours = 24) => {
  const d1 = typeof date1 === "string" ? new Date(date1) : date1;
  const d2 = typeof date2 === "string" ? new Date(date2) : date2;

  if (!(d1 instanceof Date) || isNaN(d1)) throw new Error("Invalid date1");
  if (!(d2 instanceof Date) || isNaN(d2)) throw new Error("Invalid date2");

  const diffInMs = Math.abs(d2 - d1);
  const targetMs = hours * 60 * 60 * 1000;

  return diffInMs > targetMs;
};

export const formatDateRange = (startDate, endDate, locale) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (locale.code === 'es') {
    const startDay = format(start, 'd', { locale });
    const startMonth = format(start, 'MMMM', { locale }).toLowerCase();
    const endDay = format(end, 'd', { locale });
    const endMonth = format(end, 'MMMM', { locale }).toLowerCase();
    const year = format(end, 'yyyy', { locale });
    
    if (startMonth === endMonth) {
      return `${startDay} al ${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} al ${endDay} ${endMonth} ${year}`;
    }
  } else {
    const startDay = format(start, 'd', { locale });
    const startMonth = format(start, 'MMM', { locale });
    const endDay = format(end, 'd', { locale });
    const endMonth = format(end, 'MMM', { locale });
    const year = format(end, 'yyyy', { locale });
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}–${endDay}, ${year}`;
    } else {
      return `${startMonth} ${startDay}–${endMonth} ${endDay}, ${year}`;
    }
  }
};

export const isSameUtcDay = (date1, date2) => {
  return (
    date1.getUTCDate() === date2.getUTCDate() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCFullYear() === date2.getUTCFullYear()
  );
}

export const isToday = (date, reference) => {
  return isSameUtcDay(date, reference);
}

export const isYesterday = (date, reference) => {
  const yesterday = new Date(Date.UTC(
    reference.getUTCFullYear(),
    reference.getUTCMonth(),
    reference.getUTCDate() - 1
  ));
  return isSameUtcDay(date, yesterday);
}

export const isTomorrow = (date, reference) => {
  const tomorrow = new Date(Date.UTC(
    reference.getUTCFullYear(),
    reference.getUTCMonth(),
    reference.getUTCDate() + 1
  ));
  return isSameUtcDay(date, tomorrow);
}

export const relativeDate = (date, reference = new Date(), locale) => {
  if (isToday(date)) return locale.code?.startsWith('es') ? 'hoy' : 'today'
  if (isYesterday(date)) return locale.code?.startsWith('es') ? 'ayer' : 'yesterday'
  if (isTomorrow(date)) return locale.code?.startsWith('es') ? 'mañana' : 'tomorrow'

  const diff = differenceInCalendarDays(date, reference)

  if (diff < 0) {
    return locale.code?.startsWith('es')
      ? `hace ${Math.abs(diff)} días`
      : `${Math.abs(diff)} days ago`
  }

  if (diff > 0 && diff <= 7) {
    return locale.code?.startsWith('es')
      ? `en ${diff} días`
      : `in ${diff} days`
  }

  return formatDistance(date, reference, {
    addSuffix: true,
    locale
  })
}

export const formatDateSlashed = (date) => {
  return format(new Date(date), 'dd/MM/yyyy');
};
